import type { PacketCipher } from '../protocol/packets.js';

/**
 * AES-128-GCM cipher for SSH (aes128-gcm@openssh.com).
 *
 * OpenSSH GCM mode details (RFC 5647 + OpenSSH implementation):
 * - IV is the full 12-byte value from key derivation.
 * - The IV is incremented as a big-endian 96-bit counter after each packet.
 * - The 4-byte packet_length is used as AAD (additional authenticated data).
 * - The GCM tag is 16 bytes and replaces the MAC field.
 * - The plaintext encrypted is: padding_length (1 byte) + payload + padding.
 */
export class Aes128GcmCipher implements PacketCipher {
	readonly blockSize = 16;

	private constructor(
		private sendKey: CryptoKey,
		private recvKey: CryptoKey,
		private sendIv: Uint8Array,
		private recvIv: Uint8Array,
	) {}

	/**
	 * Create a new AES-128-GCM cipher pair from derived session keys.
	 *
	 * @param encKeyC2S Client-to-server encryption key (16 bytes)
	 * @param encKeyS2C Server-to-client encryption key (16 bytes)
	 * @param ivC2S Client-to-server IV (12 bytes, used as initial counter)
	 * @param ivS2C Server-to-client IV (12 bytes, used as initial counter)
	 */
	static async create(
		encKeyC2S: Uint8Array,
		encKeyS2C: Uint8Array,
		ivC2S: Uint8Array,
		ivS2C: Uint8Array,
	): Promise<Aes128GcmCipher> {
		const [sendKey, recvKey] = await Promise.all([
			crypto.subtle.importKey('raw', new Uint8Array(encKeyC2S), 'AES-GCM', false, ['encrypt']),
			crypto.subtle.importKey('raw', new Uint8Array(encKeyS2C), 'AES-GCM', false, ['decrypt']),
		]);

		return new Aes128GcmCipher(sendKey, recvKey, ivC2S.slice(0, 12), ivS2C.slice(0, 12));
	}

	/**
	 * Increment a 12-byte IV as a big-endian 96-bit counter.
	 */
	private static incrementIv(iv: Uint8Array): void {
		for (let i = 11; i >= 0; i--) {
			iv[i] = (iv[i]! + 1) & 0xff;
			if (iv[i] !== 0) break; // no carry, stop
		}
	}

	/**
	 * Encrypt a packet.
	 *
	 * @param packetLength The packet_length value (used as AAD)
	 * @param content The plaintext: padding_length + payload + padding
	 * @param _sequenceNumber Unused for GCM (IV is the counter)
	 * @returns Ciphertext + 16-byte GCM tag
	 */
	async encrypt(
		packetLength: number,
		content: Uint8Array,
		_sequenceNumber: number,
	): Promise<Uint8Array> {
		const iv = this.sendIv.slice();

		// AAD = packet_length as 4 bytes big-endian
		const aad = new Uint8Array(4);
		new DataView(aad.buffer).setUint32(0, packetLength);

		const result = await crypto.subtle.encrypt(
			{ name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
			this.sendKey,
			new Uint8Array(content),
		);

		// increment IV for next packet
		Aes128GcmCipher.incrementIv(this.sendIv);

		return new Uint8Array(result);
	}

	/**
	 * Decrypt a packet.
	 *
	 * @param packetLength The packet_length value (used as AAD)
	 * @param ciphertext The ciphertext + 16-byte GCM tag
	 * @param _sequenceNumber Unused for GCM (IV is the counter)
	 * @returns Decrypted content: padding_length + payload + padding
	 */
	async decrypt(
		packetLength: number,
		ciphertext: Uint8Array,
		_sequenceNumber: number,
	): Promise<Uint8Array> {
		const iv = this.recvIv.slice();

		// AAD = packet_length as 4 bytes big-endian
		const aad = new Uint8Array(4);
		new DataView(aad.buffer).setUint32(0, packetLength);

		const result = await crypto.subtle.decrypt(
			{ name: 'AES-GCM', iv, additionalData: aad, tagLength: 128 },
			this.recvKey,
			new Uint8Array(ciphertext),
		);

		// increment IV for next packet
		Aes128GcmCipher.incrementIv(this.recvIv);

		return new Uint8Array(result);
	}
}
